import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, CardBody, Card } from 'reactstrap';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { LinearProgress, SnackbarContent, Link, Tooltip, Box, FormControl, InputLabel, Select, MenuItem, IconButton } from '@mui/material';
import CommentIcon from '@mui/icons-material/Comment';
import PreviewIcon from '@mui/icons-material/Preview';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import ListAltIcon from '@mui/icons-material/ListAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import OrderTrackingModal from '../OrderTrackingView/OrderTrackingModal';
import Badge from "react-bootstrap/Badge";
import OrderRemarksDetailsModel from './components/OrderRemarksDetailsModel';
import OrderDetailsShippingViewDetailsModal from 'src/containers/PnD/Order/OrderCreation/View/components/OrderDetailsShippingViewDetailsModal';
import { showNotification } from 'src/shared/components/Notification';
import { ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';
import PageHeader from 'src/shared/components/layout/PageHeader';
import RadioSelectTable from 'src/shared/components/table/RadioSelectTable';
import { safeFormatDateOnly } from 'src/utils/DateUtils';

const safeFormatDate = (value) => {
  if (!value) return '—';
  try {
    let dateVal;
    if (typeof value === 'string') {
      if (value.endsWith('Z') || value.includes('+') || (value.includes('T') && !value.endsWith('Z'))) {
        dateVal = new Date(value);
      } else {
        const utcTimestamp = value.replace(' ', 'T') + (value.endsWith('Z') ? '' : 'Z');
        dateVal = new Date(utcTimestamp);
      }
    } else {
      dateVal = new Date(value);
    }

    if (isNaN(dateVal.getTime())) {
      return String(value);
    }

    return dateVal.toLocaleString('en-GB', {
      timeZone: 'Asia/Dubai',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch (e) {
    console.error("Error formatting date:", value, e);
    return String(value);
  }
};



const PunchedOrderViewIndex = () => {
  useEffect(() => {
    getPunchedOrdersDetails();
  }, []);

  const [reactTableData, setReactTableData] = useState({ tableRowsData: [] });
  const [originalOrders, setOriginalOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [loading, setLoading] = useState(false);
  const [isNoRecordFound, setNoRecordFound] = useState(false);
  const [isStatusProgress, setStatusProgress] = useState(false);
  const [isOrderId, setOrderId] = useState(null);
  const [isOrderNo, setOrderNo] = useState(null);
  const [temp, setTempData] = useState("");
  const [viewDetailPopup, setViewDetailPopup] = useState(false);
  const [viewRemarksPopUp, setRemarksPopUp] = useState(false);
  const [isTitle, setTitle] = useState(null);
  const [isOrderIdRemarks, setOrderIdRemarks] = useState(null);

  const applyFilters = (statusVal, ordersList) => {
    let filtered = ordersList || [];

    // Filter by status
    if (statusVal !== 'all') {
      filtered = filtered.filter(r => r.orderStatus === statusVal);
    }

    setReactTableData({ tableRowsData: filtered });

    if (filtered.length > 0) {
      if (!selectedOrder || !filtered.some(r => r.orderId === selectedOrder.orderId)) {
        setSelectedOrder(filtered[0]);
      }
    } else {
      setSelectedOrder(null);
    }
  };

  const getPunchedOrdersDetails = async () => {
    try {
      setLoading(true);
      setNoRecordFound(false);
      let Obj = {};
      const response = await PndRestApi.GetPunchedOrdersDetails(Obj);
      if (response.status === 200 && response.data.status === 200) {
        const ordersList = response.data.data || [];
        setOriginalOrders(ordersList);

        // Find available unique statuses and determine default status filter
        const statuses = Array.from(new Set(ordersList.map(r => r.orderStatus).filter(Boolean)));
        let defaultStatus = statuses.find(s => s.toLowerCase().includes('transit'));
        if (!defaultStatus) {
          defaultStatus = statuses.find(s => s.toLowerCase().replace(/[^a-z]/g, '').includes('goodsreceived'));
        }
        const finalStatusFilter = defaultStatus || 'all';
        setStatusFilter(finalStatusFilter);

        applyFilters(finalStatusFilter, ordersList);
      } else if (response?.data?.status && response?.data?.status === 204) { //No Record Found
        setNoRecordFound(true);
        setOriginalOrders([]);
        setReactTableData({ tableRowsData: [] });
        setSelectedOrder(null);
      } else {
        showNotification(
          "theme-light",
          "ltr",
          `SuccessCode: ${response?.data?.status}`,
          `Message: ${response?.data?.message}`,
        );
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showNotification(
        "theme-light",
        "ltr",
        `SuccessCode: 500`,
        ERROR_MESSAGES.tryCatchError,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLinkClick = (orderId, orderNo) => {
    setOrderNo(orderNo)
    setOrderId(orderId);
    setStatusProgress(true);
  };

  const uniqueStatuses = useMemo(() => {
    const statuses = originalOrders.map(r => r.orderStatus).filter(Boolean);
    return Array.from(new Set(statuses)).sort((a, b) => a.localeCompare(b));
  }, [originalOrders]);

  const viewDetails = async (event) => {
    if (event?.orderId) {
      setTempData(event);
      setViewDetailPopup(true);
      setTitle("View-Order Details")
    }
  }

  const viewRemarks = async (event) => {
    if (event?.orderId) {
      setOrderIdRemarks(event?.orderId);
      setRemarksPopUp(true);
      setTitle("View-Order Remarks Details")
    }
  }

  const ORDER_COLUMNS = useMemo(() => [
    {
      key: 'orderNumber',
      header: 'Order No.',
      sortable: true,
      width: '140px',
      render: (value, row) => (
        <Tooltip title="Click here to view the Order Status">
          <Link
            component="button"
            variant="body2"
            onClick={(e) => {
              e.stopPropagation();
              handleLinkClick(row.orderId, row.orderNumber);
            }}
            style={{ textDecoration: 'none', fontWeight: 600, textAlign: 'left' }}
          >
            {value}
          </Link>
        </Tooltip>
      )
    },
    
    {
      key: 'erpOrderNo',
      header: 'ERP No.',
      sortable: true,
      width: '130px',
    },
    {
      key: 'requestedDateTimeSlot',
      header: 'Req. Date',
      sortable: true,
      width: '150px',
      render: (value) => value ? safeFormatDateOnly(value) : '—',
    },
    {
      key: 'reqTimeSlot',
      header: 'Time Slot',
      sortable: true,
      width: '150px',
    },
    {
      key: 'orderStatus',
      header: 'Status',
      sortable: true,
      width: '110px',
    },
   
    {
      key: 'areaName',
      header: 'Area',
      sortable: true,
      width: '120px',
    },
     {
      key: 'riderName',
      header: 'Rider',
      sortable: true,
      width: '140px',
    },
  ], []);

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <Container fluid style={{ maxWidth: '1400px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <PageHeader
            icon={<ListAltIcon fontSize="large" />}
            title="Punched Order Details"
            subtitle="View order details and monitor customer orders that are currently being processed."
            gradient="linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
          />
          <Tooltip title="Refresh Details">
            <IconButton
              onClick={getPunchedOrdersDetails}
              disabled={loading}
              sx={{
                mt: 1,
                backgroundColor: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                color: '#0284c7',
                '&:hover': {
                  backgroundColor: '#f1f5f9',
                },
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

        <Row style={{ marginTop: '20px' }}>
          {/* Left Panel: Master List */}
          <Col md={7} lg={7}>
            {!isNoRecordFound && uniqueStatuses.length > 0 && (
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="order-status-filter-label">Order Status</InputLabel>
                  <Select
                    labelId="order-status-filter-label"
                    value={statusFilter}
                    label="Order Status"
                    onChange={(e) => {
                      const statusVal = e.target.value;
                      setStatusFilter(statusVal);
                      applyFilters(statusVal, originalOrders);
                    }}
                    sx={{ borderRadius: 2, bgcolor: 'white' }}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    {uniqueStatuses.map(status => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {!isNoRecordFound ? (
              <RadioSelectTable
                rows={reactTableData.tableRowsData || []}
                columns={ORDER_COLUMNS}
                rowKey="orderId"
                selectedKey={selectedOrder?.orderId}
                onSelect={setSelectedOrder}
                searchKeys={['orderNumber', 'erpOrderNo', 'erpNo', 'invoiceNo', 'orderStatus', 'riderName']}
                searchPlaceholder="Search orders..."
                pageSizes={[5, 10, 20, 50]}
                minWidth="1000px"
              />
            ) : (
              <Card style={{ borderRadius: '12px', border: '1px solid #e2e8f0', padding: '40px', textAlign: 'center' }}>
                <p className="text-muted m-0">No punched orders found.</p>
              </Card>
            )}
          </Col>

          {/* Right Panel: Sleek Detail Card */}
          <Col md={5} lg={5}>
            <Card style={{
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
              minHeight: '520px',
              background: '#ffffff',
              overflow: 'hidden',
            }}>
              {selectedOrder ? (
                <>
                  {/* Header Banner */}
                  <div style={{
                    background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                    padding: '24px 20px',
                    color: '#ffffff'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <h4 style={{ margin: 0, fontWeight: 800, fontSize: '1.2rem', color: '#ffffff' }}>
                          {selectedOrder.orderNumber}
                        </h4>
                        <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)' }}>
                          Invoice: {selectedOrder.invoiceNo || '—'}
                        </span>
                      </div>
                      <div>
                        <span
                          className="badge bg-white text-dark"
                          style={{ fontSize: '0.75rem', fontWeight: 700, padding: '5px 10px', borderRadius: '12px' }}
                        >
                          {selectedOrder.orderStatus || 'PUNCHED'}
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                      <Tooltip title="View Order Details">
                        <IconButton
                          size="small"
                          onClick={() => viewDetails(selectedOrder)}
                          style={{ color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.15)' }}
                        >
                          <PreviewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Remarks">
                        <IconButton
                          size="small"
                          onClick={() => viewRemarks(selectedOrder)}
                          style={{ color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.15)' }}
                        >
                          <CommentIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Track Order Status">
                        <IconButton
                          size="small"
                          onClick={() => handleLinkClick(selectedOrder.orderId, selectedOrder.orderNumber)}
                          style={{ color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.15)' }}
                        >
                          <DirectionsBikeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>

                  {/* Details Body */}
                  <CardBody style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                      {/* General Info */}
                      <div>
                        <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                          General Info
                        </h6>
                        <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted small">Rider Assigned:</span>
                            <span className="fw-bold small text-dark">{selectedOrder.riderName || 'Not Assigned'}</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted small">Order Type:</span>
                            <span className="fw-bold small">
                              {selectedOrder.orderType === 1 ? (
                                <Badge bg="success">DROP OFF</Badge>
                              ) : selectedOrder.orderType === 2 ? (
                                <Badge bg="primary">PICK UP</Badge>
                              ) : (
                                <Badge bg="warning">PnD</Badge>
                              )}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted small">Order Date:</span>
                            <span className="fw-bold small text-dark">{safeFormatDate(selectedOrder.createdDateTime)}</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted small">Requested Date:</span>
                            <span className="fw-bold small text-dark">{selectedOrder.requestedDateTimeSlot ? safeFormatDateOnly(selectedOrder.requestedDateTimeSlot) : '—'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Route Details */}
                      <div>
                        <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                          Route & Logistics
                        </h6>
                        <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <div>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span className="text-muted small fw-bold">From:</span>
                              <span>
                                {selectedOrder.fromTypeId === 1 ? (
                                  <Badge bg="secondary">Own Hub</Badge>
                                ) : selectedOrder.fromTypeId === 2 ? (
                                  <Badge bg="secondary">Hub Customer</Badge>
                                ) : selectedOrder.fromTypeId === 3 ? (
                                  <Badge bg="secondary">Merchant</Badge>
                                ) : selectedOrder.fromTypeId === 4 ? (
                                  <Badge bg="secondary">Another Hub</Badge>
                                ) : <Badge bg="secondary">Merchant Customer</Badge>}
                              </span>
                            </div>
                            <span className="small text-dark d-block fw-semibold">{selectedOrder.fromName || '—'}</span>
                          </div>

                          <div style={{ borderTop: '1px dashed #e2e8f0', paddingTop: '8px' }}>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span className="text-muted small fw-bold">To:</span>
                              <span>
                                {selectedOrder.toTypeId === 1 ? (
                                  <Badge bg="secondary">Own Hub</Badge>
                                ) : selectedOrder.toTypeId === 2 ? (
                                  <Badge bg="secondary">Hub Customer</Badge>
                                ) : selectedOrder.toTypeId === 3 ? (
                                  <Badge bg="secondary">Merchant</Badge>
                                ) : selectedOrder.toTypeId === 4 ? (
                                  <Badge bg="secondary">Another Hub</Badge>
                                ) : <Badge bg="secondary">Merchant Customer</Badge>}
                              </span>
                            </div>
                            <span className="small text-dark d-block fw-semibold">{selectedOrder.toName || '—'}</span>
                          </div>
                        </div>
                      </div>

                    </div>
                  </CardBody>
                </>
              ) : (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', padding: '40px', textAlign: 'center', minHeight: '520px',
                }}>
                  <div style={{ fontSize: '3.5rem', color: '#cbd5e1', marginBottom: '16px' }}>📦</div>
                  <h5 className="fw-bold text-muted">No Order Selected</h5>
                  <p className="text-muted small">Select an order from the list to view detailed information.</p>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Container>

      {isNoRecordFound && <SnackbarContent message={'No Record Found'} sx={{ mt: 2 }} />}
      {isStatusProgress && <OrderTrackingModal modalShow={isStatusProgress} isOrderId={isOrderId} isOrderNo={isOrderNo} setModalShow={setStatusProgress} />}

      {viewDetailPopup &&
        <OrderDetailsShippingViewDetailsModal modalShow={viewDetailPopup}
          setModalShow={setViewDetailPopup}
          dataInfo={temp}
          isTitle={isTitle} />
      }
      {viewRemarksPopUp &&
        <OrderRemarksDetailsModel modalShow={viewRemarksPopUp} isOrderIdRemarks={isOrderIdRemarks}
          setModalShow={setRemarksPopUp}
          isTitle={isTitle} />
      }
    </Box>
  );
};

export default PunchedOrderViewIndex;